import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  fetchMiscQuickBooksTimeClockStatus,
  formatMiscQuickBooksTimeInstant,
  MISC_QBT_CLOCK_STATUS_QUERY_KEY,
  postMiscQuickBooksTimeBreakEnd,
  postMiscQuickBooksTimeBreakStart,
  postMiscQuickBooksTimeClockIn,
  postMiscQuickBooksTimeClockOut,
  postMiscQuickBooksTimeLunchEnd,
  postMiscQuickBooksTimeLunchStart,
  readMiscQuickBooksTimeClockLocation,
} from "@/lib/miscQuickBooksTimeClockApi";
import {
  fetchMiscTimeCardPayPeriods,
  formatMiscTimeCardDuration,
  formatMiscTimeCardInstant,
  MISC_QBT_TIME_CARDS_QUERY_KEY,
  postMiscCancelTimeCardChangeRequest,
  postMiscTimeCardChangeRequest,
  type MiscTimeCardPeriodSection,
  type MiscTimeCardRow,
} from "@/lib/miscQuickBooksTimeCardsApi";
import {
  PersonalTimeCardChangeRequestModal,
  type PersonalTimeCardChangeRequestModalMode,
} from "./PersonalTimeCardChangeRequestModal";

const PAGE_HELP =
  "Clock in and out on QuickBooks Time from the hub. Work time uses job code 0 unless the server sets QBT_DEFAULT_JOBCODE_ID. Start and end lunch on the Meal (unpaid) job code in QuickBooks Time. Short breaks stay hub-local for the daily task report. Below the clock card, review your current and previous pay period time entries, request corrections, or cancel a pending request before an admin acts on it. Hub administrators approve or reject requests under Admin → Time card changes. On a phone, allow location when prompted; on a desktop or shop PC the hub sends configured site coordinates with your device label and does not ask the browser for GPS. Connect QuickBooks Time from the banner at the top of the hub if OAuth is missing.";

function statusBadgeClass(onTheClock: boolean, onLunch: boolean, onBreak: boolean): string {
  if (onBreak) {
    return "bg-amber-100 text-amber-900 border-amber-200";
  }
  if (onLunch) {
    return "bg-sky-100 text-sky-900 border-sky-200";
  }
  if (onTheClock) {
    return "bg-emerald-100 text-emerald-900 border-emerald-200";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function statusLabel(onTheClock: boolean, onLunch: boolean, onBreak: boolean): string {
  if (onBreak) {
    return "On break";
  }
  if (onLunch) {
    return "On lunch";
  }
  if (onTheClock) {
    return "On the clock";
  }
  return "Off the clock";
}

export type PersonalTimeClockActionMode = "off_clock" | "on_clock" | "on_lunch" | "on_break";

export function resolvePersonalTimeClockActionMode(input: {
  onTheClock: boolean;
  onLunch: boolean;
  onBreak: boolean;
}): PersonalTimeClockActionMode {
  if (input.onBreak) {
    return "on_break";
  }
  if (input.onLunch) {
    return "on_lunch";
  }
  if (input.onTheClock) {
    return "on_clock";
  }
  return "off_clock";
}

function pendingSpinner(isPending: boolean): ReactNode {
  return isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null;
}

function formatPeriodRange(section: MiscTimeCardPeriodSection): string {
  return `${section.period.startDate} – ${section.period.endDate}`;
}

function MiscTimeCardPeriodTable({
  section,
  onRequestChange,
  onRequestDelete,
  onRequestAdd,
  onCancelRequest,
  cancellingRequestId,
}: {
  section: MiscTimeCardPeriodSection;
  onRequestChange: (row: MiscTimeCardRow) => void;
  onRequestDelete: (row: MiscTimeCardRow) => void;
  onRequestAdd: (calendarDate: string) => void;
  onCancelRequest: (requestId: number) => void;
  cancellingRequestId: number | null;
}) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{section.period.label}</h2>
            <p className="text-sm text-muted-foreground">{formatPeriodRange(section)}</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onRequestAdd(section.period.endDate)}
          >
            Request missing time
          </Button>
        </div>

        {section.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No time entries in this pay period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Start</th>
                  <th className="px-2 py-2">End</th>
                  <th className="px-2 py-2">Duration</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <tr key={row.rowKey} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-2 py-2 tabular-nums text-slate-700">{row.calendarDate}</td>
                    <td className="px-2 py-2 text-slate-900">{row.label}</td>
                    <td className="px-2 py-2 tabular-nums text-slate-700">
                      {formatMiscTimeCardInstant(row.start)}
                    </td>
                    <td className="px-2 py-2 tabular-nums text-slate-700">
                      {formatMiscTimeCardInstant(row.end)}
                    </td>
                    <td className="px-2 py-2 tabular-nums text-slate-700">
                      {formatMiscTimeCardDuration(row.durationSeconds)}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {row.pendingChangeRequestId != null ? (
                          <>
                            <Badge
                              tone="warning"
                              className="whitespace-nowrap border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-900"
                            >
                              Pending review
                            </Badge>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={
                                cancellingRequestId != null &&
                                cancellingRequestId === row.pendingChangeRequestId
                              }
                              onClick={() => onCancelRequest(row.pendingChangeRequestId as number)}
                            >
                              {cancellingRequestId === row.pendingChangeRequestId ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              ) : null}
                              Cancel request
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => onRequestChange(row)}
                            >
                              Request change
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => onRequestDelete(row)}
                            >
                              Request delete
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function PersonalTimeClockPage() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<PersonalTimeCardChangeRequestModalMode>("edit");
  const [modalRow, setModalRow] = useState<MiscTimeCardRow | null>(null);
  const [modalCalendarDate, setModalCalendarDate] = useState("");

  const statusQuery = useQuery({
    queryKey: MISC_QBT_CLOCK_STATUS_QUERY_KEY,
    queryFn: fetchMiscQuickBooksTimeClockStatus,
    refetchInterval: 30_000,
  });

  const timeCardsQuery = useQuery({
    queryKey: MISC_QBT_TIME_CARDS_QUERY_KEY,
    queryFn: fetchMiscTimeCardPayPeriods,
    refetchInterval: 60_000,
  });

  const status = statusQuery.data;
  const timeCards = timeCardsQuery.data;

  const invalidateStatus = async () => {
    await queryClient.invalidateQueries({ queryKey: MISC_QBT_CLOCK_STATUS_QUERY_KEY });
  };

  const invalidateTimeCards = async () => {
    await queryClient.invalidateQueries({ queryKey: MISC_QBT_TIME_CARDS_QUERY_KEY });
  };

  const [modalSubmitError, setModalSubmitError] = useState<string | null>(null);

  const changeRequestMutation = useMutation({
    mutationFn: postMiscTimeCardChangeRequest,
    onSuccess: async () => {
      setModalSubmitError(null);
      setMessage({ type: "success", text: "Time card change request submitted for admin review." });
      setModalOpen(false);
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      const text = error.message || "Could not submit change request.";
      setModalSubmitError(text);
      setMessage({ type: "error", text });
    },
  });

  const cancelRequestMutation = useMutation({
    mutationFn: postMiscCancelTimeCardChangeRequest,
    onSuccess: async () => {
      setMessage({ type: "success", text: "Time card change request cancelled." });
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Could not cancel request." });
    },
  });

  const clockInMutation = useMutation({
    mutationFn: async () => {
      const location = await readMiscQuickBooksTimeClockLocation();
      return postMiscQuickBooksTimeClockIn(location);
    },
    onSuccess: async () => {
      setMessage({ type: "success", text: "Clocked in on QuickBooks Time." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Clock in failed." });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: async () => {
      const location = await readMiscQuickBooksTimeClockLocation();
      return postMiscQuickBooksTimeClockOut(location);
    },
    onSuccess: async () => {
      setMessage({ type: "success", text: "Clocked out on QuickBooks Time." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Clock out failed." });
    },
  });

  const lunchStartMutation = useMutation({
    mutationFn: async () => {
      const location = await readMiscQuickBooksTimeClockLocation();
      return postMiscQuickBooksTimeLunchStart(location);
    },
    onSuccess: async () => {
      setMessage({ type: "success", text: "Lunch started on QuickBooks Time." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Could not start lunch." });
    },
  });

  const lunchEndMutation = useMutation({
    mutationFn: async () => {
      const location = await readMiscQuickBooksTimeClockLocation();
      return postMiscQuickBooksTimeLunchEnd(location);
    },
    onSuccess: async () => {
      setMessage({ type: "success", text: "Lunch ended; clocked back in on work." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Could not end lunch." });
    },
  });

  const breakStartMutation = useMutation({
    mutationFn: postMiscQuickBooksTimeBreakStart,
    onSuccess: async () => {
      setMessage({ type: "success", text: "Break started (hub only)." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Could not start break." });
    },
  });

  const breakEndMutation = useMutation({
    mutationFn: postMiscQuickBooksTimeBreakEnd,
    onSuccess: async () => {
      setMessage({ type: "success", text: "Break ended and logged for daily task reporting." });
      await invalidateStatus();
      await invalidateTimeCards();
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Could not end break." });
    },
  });

  const busy =
    clockInMutation.isPending ||
    clockOutMutation.isPending ||
    lunchStartMutation.isPending ||
    lunchEndMutation.isPending ||
    breakStartMutation.isPending ||
    breakEndMutation.isPending;

  const readiness = useMemo(() => {
    if (status == null) {
      return { ready: false, reason: null as string | null };
    }
    if (!status.oauthConnected) {
      return { ready: false, reason: "QuickBooks Time is not connected on this hub." };
    }
    if (!status.quickbooksTimeUserMatched) {
      return { ready: false, reason: "Your hub login is not matched to a QuickBooks Time user." };
    }
    return { ready: true, reason: null };
  }, [status]);

  const actionMode =
    status != null
      ? resolvePersonalTimeClockActionMode({
          onTheClock: status.onTheClock,
          onLunch: status.onLunch,
          onBreak: status.onBreak,
        })
      : null;

  const actionsEnabled = readiness.ready && actionMode != null;

  const openModal = (
    mode: PersonalTimeCardChangeRequestModalMode,
    row: MiscTimeCardRow | null,
    calendarDate: string,
  ) => {
    setModalMode(mode);
    setModalRow(row);
    setModalCalendarDate(calendarDate);
    setModalSubmitError(null);
    setModalOpen(true);
    setMessage(null);
  };

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Time clock</PageTitleWithHelp>

      <Card>
        <CardBody className="space-y-4">
          {statusQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading clock status…
            </div>
          ) : statusQuery.isError ? (
            <p className="text-sm text-destructive">Failed to load clock status.</p>
          ) : status != null ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={cn(
                    "inline-flex rounded-full border px-3 py-1 text-sm font-medium",
                    statusBadgeClass(status.onTheClock, status.onLunch, status.onBreak),
                  )}
                >
                  {statusLabel(status.onTheClock, status.onLunch, status.onBreak)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {status.calendarDate} ({status.timeZone})
                </span>
              </div>

              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Work clock in</dt>
                  <dd>{formatMiscQuickBooksTimeInstant(status.clockIn)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Lunch started</dt>
                  <dd>{formatMiscQuickBooksTimeInstant(status.lunchStartedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Break started</dt>
                  <dd>{formatMiscQuickBooksTimeInstant(status.breakStartedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">QuickBooks Time user</dt>
                  <dd>{status.quickbooksTimeUserId ?? "Not matched"}</dd>
                </div>
              </dl>

              {!readiness.ready && readiness.reason != null ? (
                <p className="text-sm text-amber-800">{readiness.reason}</p>
              ) : null}
            </>
          ) : null}

          {message != null ? (
            <p
              className={cn(
                "text-sm",
                message.type === "success" ? "text-emerald-700" : "text-destructive",
              )}
            >
              {message.text}
            </p>
          ) : null}

          {actionsEnabled ? (
            <div className="flex flex-wrap gap-2">
              {actionMode === "off_clock" ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setMessage(null);
                    clockInMutation.mutate();
                  }}
                >
                  {pendingSpinner(clockInMutation.isPending)}
                  Clock in
                </Button>
              ) : null}

              {actionMode === "on_clock" ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setMessage(null);
                      clockOutMutation.mutate();
                    }}
                  >
                    {pendingSpinner(clockOutMutation.isPending)}
                    Clock out
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setMessage(null);
                      lunchStartMutation.mutate();
                    }}
                  >
                    {pendingSpinner(lunchStartMutation.isPending)}
                    Start lunch
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setMessage(null);
                      breakStartMutation.mutate();
                    }}
                  >
                    {pendingSpinner(breakStartMutation.isPending)}
                    Start break
                  </Button>
                </>
              ) : null}

              {actionMode === "on_lunch" ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setMessage(null);
                    lunchEndMutation.mutate();
                  }}
                >
                  {pendingSpinner(lunchEndMutation.isPending)}
                  End lunch
                </Button>
              ) : null}

              {actionMode === "on_break" ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setMessage(null);
                    breakEndMutation.mutate();
                  }}
                >
                  {pendingSpinner(breakEndMutation.isPending)}
                  End break
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>

      {timeCardsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading pay period time cards…
        </div>
      ) : timeCardsQuery.isError ? (
        <p className="text-sm text-destructive">Failed to load pay period time cards.</p>
      ) : timeCards != null ? (
        <>
          <MiscTimeCardPeriodTable
            section={timeCards.current}
            onRequestChange={(row) => openModal("edit", row, row.calendarDate)}
            onRequestDelete={(row) => openModal("delete", row, row.calendarDate)}
            onRequestAdd={(calendarDate) => openModal("add", null, calendarDate)}
            onCancelRequest={(requestId) => {
              setMessage(null);
              cancelRequestMutation.mutate(requestId);
            }}
            cancellingRequestId={
              cancelRequestMutation.isPending ? (cancelRequestMutation.variables ?? null) : null
            }
          />
          <MiscTimeCardPeriodTable
            section={timeCards.previous}
            onRequestChange={(row) => openModal("edit", row, row.calendarDate)}
            onRequestDelete={(row) => openModal("delete", row, row.calendarDate)}
            onRequestAdd={(calendarDate) => openModal("add", null, calendarDate)}
            onCancelRequest={(requestId) => {
              setMessage(null);
              cancelRequestMutation.mutate(requestId);
            }}
            cancellingRequestId={
              cancelRequestMutation.isPending ? (cancelRequestMutation.variables ?? null) : null
            }
          />
        </>
      ) : null}

      <PersonalTimeCardChangeRequestModal
        open={modalOpen}
        mode={modalMode}
        row={modalRow}
        calendarDate={modalCalendarDate}
        submitting={changeRequestMutation.isPending}
        submitError={modalSubmitError}
        onCancel={() => {
          setModalOpen(false);
          setModalSubmitError(null);
        }}
        onSubmit={(body) => changeRequestMutation.mutate(body)}
      />
    </div>
  );
}
